import { zodResolver } from '@hookform/resolvers/zod';
import { Send } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '../../../components/Button';
import { Textarea } from '../../../components/Textarea';
import { cn } from '../../../lib/cn';
import { usePostThreadMutation } from '../threads.api';
import type { ThreadTarget } from '../threads.schema';
import { ReplyFormSchema, type ReplyFormValues } from '../threads.schema';

interface Props {
    target: ThreadTarget;
    parentId?: number | null;
    onPosted?: () => void;
    autoFocus?: boolean;
    placeholder?: string;
}

export function ReplyForm({
    target,
    parentId = null,
    onPosted,
    autoFocus = false,
    placeholder,
}: Props) {
    const [postThread, { isLoading }] = usePostThreadMutation();

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm<ReplyFormValues>({
        resolver: zodResolver(ReplyFormSchema),
        defaultValues: { message: '' },
    });

    const isRoot = parentId == null;
    const message = watch('message') ?? '';
    const isEmpty = message.trim().length === 0;

    const onSubmit = handleSubmit(async (values) => {
        try {
            await postThread({
                target,
                parentId,
                message: values.message,
                params: null,
            }).unwrap();
            reset({ message: '' });
            onPosted?.();
        } catch (e) {
            const err = e as { message?: string };
            toast.error(err?.message === 'unauthorized' ? 'Neautorizat' : 'Eroare la postare');
        }
    });

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-2">
            <Textarea
                rows={isRoot ? 3 : 2}
                placeholder={placeholder ?? (isRoot ? 'Descrie problema...' : 'Scrie răspunsul...')}
                autoFocus={autoFocus}
                aria-invalid={!!errors.message}
                className={cn(errors.message && 'border-red-400 focus-visible:ring-red-500')}
                {...register('message')}
            />
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    {errors.message ? (
                        <span className="text-red-600 dark:text-red-400">{errors.message.message}</span>
                    ) : isRoot ? (
                        'Mesajul deschide o nouă problemă pe această comandă.'
                    ) : (
                        'Răspunde la problema de mai sus.'
                    )}
                </span>
                <Button type="submit" size="sm" disabled={isLoading || isEmpty}>
                    <Send aria-hidden="true" className="h-4 w-4" />
                    {isLoading ? 'Se trimite...' : isRoot ? 'Deschide' : 'Trimite'}
                </Button>
            </div>
        </form>
    );
}
